import { FC, FormEvent, useState } from 'react';

import { Button, CircularProgress, Divider, Grid, Paper, TextField, Typography } from '@mui/material';
import { Inventory } from '@mui/icons-material';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import axios from '../../../sharedModules/utils/axios.utils';

import { getWarehouseSelectedLocation } from '../../navigation/components/WarehouseLocationToggle';
import WarehouseSelect from '../../warehouseOptions/components/WarehouseSelect';
import PurchaseOrderBanners from '../components/PurchaseOrderBanners';
import { PurchaseOrderLookup } from '../model';

const LOCATION_FIELDS = ['rack', 'shelf', 'bin'] as const;

type LocationField = (typeof LOCATION_FIELDS)[number];

const LOCATION_LABELS: Record<LocationField, string> = {
  rack: 'Rack',
  shelf: 'Shelf',
  bin: 'Bin',
};

const EMPTY_LOCATION: Record<LocationField, string> = { rack: '', shelf: '', bin: '' };
const NO_PREFILL: Record<LocationField, boolean> = { rack: false, shelf: false, bin: false };

// PO Number and Count stay digits-only so barcode guns do not inject e/+/−/. from type=number quirks.
// Rack/Shelf/Bin accept alphanumeric ServMan location codes as returned by lookup.
const digitsOnly = (value: string): string => value.replace(/\D/g, '');

const PREFILLED_STYLE = { '& .MuiOutlinedInput-root': { backgroundColor: '#FFF9C4' } };

const ReceivingPage: FC = () => {
  const [poNumber, setPoNumber] = useState<string>('');
  const [count, setCount] = useState<string>('');
  // Held for the future receipt POST; submitting is a separate ticket.
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [locationFields, setLocationFields] = useState<Record<LocationField, string>>(EMPTY_LOCATION);
  // Tracks which values came from the PO rather than from the receiver, so the yellow highlight
  // never claims credit for something they typed themselves.
  const [prefilled, setPrefilled] = useState<Record<LocationField, boolean>>(NO_PREFILL);
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrderLookup>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleLocationChange = (field: LocationField, value: string) => {
    setLocationFields((previous) => ({ ...previous, [field]: value }));
    setPrefilled((previous) => ({ ...previous, [field]: false }));
  };

  const applyLookup = (result: PurchaseOrderLookup) => {
    setPurchaseOrder(result);
    setLocationFields({
      rack: result.rack || '',
      shelf: result.shelf || '',
      bin: result.bin || '',
    });
    setPrefilled({
      rack: !!result.rack,
      shelf: !!result.shelf,
      bin: !!result.bin,
    });
    setErrorMessage('');
  };

  // On failure: clear PO banners and prior PO location so receivers never shelf against a stale
  // suggestion. Preserve Count only — that is the receiver's own entry, not the PO's.
  const clearLookupSuggestion = () => {
    setPurchaseOrder(undefined);
    setLocationFields(EMPTY_LOCATION);
    setPrefilled(NO_PREFILL);
  };

  const handleLookUp = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      // Match the backend LOOKUP_TIMEOUT (15s) with a small buffer for round-trip.
      const response = await axios.get<PurchaseOrderLookup>(`/receiving/purchase_orders/${poNumber}`, {
        params: { location: getWarehouseSelectedLocation() },
        timeout: 20000,
      });
      applyLookup(response.data);
    } catch (error: any) {
      clearLookupSuggestion();
      setErrorMessage(error?.response?.data?.error || 'Something went wrong looking up this PO.');
    } finally {
      setLoading(false);
    }
  };

  const anyPrefilled = LOCATION_FIELDS.some((field) => prefilled[field]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader titleIcon={<Inventory />} mainTitle="Receiving" />
      </Grid>
      <Grid item xs={12}>
        <ErrorMessage message={errorMessage} />
      </Grid>
      <Grid item xs={12}>
        <Paper style={{ padding: 16 }}>
          <form onSubmit={handleLookUp}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm>
                <TextField
                  fullWidth
                  autoFocus
                  label="PO Number"
                  value={poNumber}
                  onChange={(event) => setPoNumber(digitsOnly(event.target.value))}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                />
              </Grid>
              <Grid item>
                <Button type="submit" variant="contained" size="large" disabled={loading || !poNumber}>
                  {loading ? <CircularProgress size={24} /> : 'Look Up'}
                </Button>
              </Grid>
            </Grid>
          </form>
          <Divider style={{ margin: '24px 0' }} />
          {purchaseOrder && (
            <div style={{ marginBottom: 24 }}>
              <PurchaseOrderBanners purchaseOrder={purchaseOrder} />
            </div>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <WarehouseSelect
                value={warehouseId}
                onChange={setWarehouseId}
                location={getWarehouseSelectedLocation()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Count"
                value={count}
                onChange={(event) => setCount(digitsOnly(event.target.value))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
            </Grid>
            {LOCATION_FIELDS.map((field) => (
              <Grid item xs={12} sm={6} md={3} key={field}>
                <TextField
                  fullWidth
                  label={LOCATION_LABELS[field]}
                  value={locationFields[field]}
                  onChange={(event) => handleLocationChange(field, event.target.value)}
                  inputProps={{ autoCapitalize: 'characters' }}
                  sx={prefilled[field] ? PREFILLED_STYLE : undefined}
                />
              </Grid>
            ))}
          </Grid>
          {anyPrefilled && (
            <Typography variant="caption" color="text.secondary" style={{ display: 'block', marginTop: 8 }}>
              Highlighted fields were prefilled from the PO. Edit any of them to override.
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ReceivingPage;
